import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import SegmentedProgress from "../components/ui/SegmantProgress";
import {
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Calendar,
  BookOpen,
} from "lucide-react";
import { useAuth } from "../App";
import { tokenService } from "../services/TokenService";
import { useNavigate } from "react-router-dom";
import { useEnhancedProjectsQuery } from "../api/projectManagementApi";
import RecentSubmissionCard from "../components/RecentSubmissionCard";

interface Project {
  id: string;
  title: string;
  description?: string;
  status?: string;
  progress?: any;
  dueDate: string | null;
  startDate?: string | null;
  completedCheckPointCount?: number;
  inProgressCheckPointCount?: number;
  submittedCheckPointCount?: number;
  totalCheckPoints?: number;
  totalCheckpoints?: number;
}

interface ProjectCardProps {
  project: Project;
  onViewProject: (projectId: string) => void;
}

function ProjectCard({ project, onViewProject }: ProjectCardProps) {
  const handleViewProject = (e: React.MouseEvent) => {
    e.stopPropagation();
    onViewProject(project.id);
  };

  const getDueDateDisplay = () => {
    const now = new Date();
    const due = project.dueDate ? new Date(project.dueDate) : null;

    if (!due) return { text: "No due date", isOverdue: false };

    const isOverdue = now > due;

    const formattedEndDate = `${due.getDate()} ${due.toLocaleString("default", {
      month: "short",
    })} ${due.getFullYear()}`;

    return { text: formattedEndDate, isOverdue };
  };

  const dueDateDisplay = getDueDateDisplay();

  return (
    <Card onClick={handleViewProject} className="overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 border border-border rounded-lg h-full flex flex-col">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold pr-2 line-clamp-2">
            {project?.title}
          </CardTitle>
        </div>
        <div>
          <div className="text-sm text-muted-foreground mt-2 line-clamp-3 overflow-hidden">
            <div
              className="text-bits-grey-600
  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2
  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2
  [&_li]:mb-1 [&_p]:mb-2"
              dangerouslySetInnerHTML={{
                __html:
                  project?.description || "<p>No description specified</p>",
              }}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 flex flex-col grow overflow-visible">
        {/* Added overflow-visible ^ */}
        <div className="space-y-4 flex flex-col justify-between h-full overflow-visible">
          <div className="space-y-2 relative">
            {" "}
            {/* Added relative to help tooltip reference */}
            <span className="text-base text-muted-foreground">Progress</span>
            <SegmentedProgress
              completedCheckPointCount={project?.completedCheckPointCount || 0}
              inProgressCheckPointCount={
                project?.inProgressCheckPointCount || 0
              }
              submittedCheckPointCount={project?.submittedCheckPointCount || 0}
              totalCheckPoints={
                project?.totalCheckPoints || project?.totalCheckpoints || 0
              }
            />
          </div>
          <div className="flex items-center justify-between pt-2 mt-auto">
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-muted-foreground font-semibold">
                {dueDateDisplay.text}
                {dueDateDisplay.isOverdue && (
                  <span className="text-bits-red ml-1">(Overdue)</span>
                )}
              </span>
            </div>
            <Button
              onClick={handleViewProject}
              className="bg-bits-green hover:bg-green-700 whitespace-nowrap text-white px-4 py-2 rounded-md"
            >
              <ExternalLink className="h-4 w-4" />
              Open
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Main StudentDashboard component
export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(tokenService.getToken());

  useEffect(() => {
    const unsubscribe = tokenService.subscribe((newToken) => {
      setToken(newToken);
    });
    return unsubscribe;
  }, []);

  const {
    data: projectsData,
    isLoading: isProjectsLoading,
    isError: isProjectsError,
    refetch: refetchProjects,
  } = useEnhancedProjectsQuery({ token }, { retry: false });

  const handleViewProject = (projectId: string) => {
    navigate(`/project-details/${projectId}`, {
      state: { from: "dashboard" },
    });
  };

  const handleViewAllProjects = () => {
    navigate("/allprojects", {
      state: { from: "dashboard" },
    });
  };

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <div className="mb-8 mt-4">
        <h1 className="text-3xl font-semibold text-black mb-2">
          Welcome back, {user?.name}
        </h1>
        <p className="text-gray-600">Here's an overview of your projects</p>
      </div>

      {/* Projects Section */}
      {isProjectsLoading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : isProjectsError ? (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <div>
                <h3 className="font-semibold text-red-900">
                  Failed to load projects
                </h3>
                <p className="text-sm text-red-700 mt-1">Please try again.</p>
                <Button
                  onClick={() => refetchProjects()}
                  variant="outline"
                  size="sm"
                  className="mt-3"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <Card className="border border-border rounded-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-lg font-semibold">
                    <BookOpen className="h-5 w-5 text-bits-blue" />
                    Your Projects
                  </CardTitle>
                  <button
                    onClick={handleViewAllProjects}
                    className="hover:underline border whitespace-nowrap px-2 text-lg font-semibold rounded-md flex items-center gap-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View All
                  </button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-6">
                  {(projectsData?.projects || []).length > 0 ? (
                    (projectsData?.projects || [])
                      .slice(0, 3)
                      .map((project) => (
                        <ProjectCard
                          key={project.id}
                          project={project as any}
                          onViewProject={handleViewProject}
                        />
                      ))
                  ) : (
                    <div className="p-6 text-center">
                      <div className="text-lg font-medium text-gray-600">
                        No projects found
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <RecentSubmissionCard />
          </div>
        </div>
      )}
    </div>
  );
}
